import {
  Forward10SecSVG,
  PauseButtonSVG,
  RecordButtonSVG,
  Rewind10SecSVG,
} from "@/utils/svg";
import { PauseCircle, PlayCircle, Video } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./button";
import Modal from "./Modal";

interface VideoRecordingModalProps {
  onClose: () => void;
  onSave: (blob: File) => void;
  maxRecordingDurationSeconds?: number;
}

const VideoRecordingModal: React.FC<VideoRecordingModalProps> = ({
  onClose,
  onSave,
  maxRecordingDurationSeconds = 300,
}) => {
  // State variables
  const [recordingTime, setRecordingTime] = useState(0);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);

  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // Video playback controls
  const togglePlayback = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      if (!videoElement) return;

      if (isPlaying) {
        videoElement.pause();
        setIsPlaying(false);

        // Clear playback timer when paused
        if (playbackIntervalRef.current) {
          clearInterval(playbackIntervalRef.current);
          playbackIntervalRef.current = null;
        }
      } else {
        videoElement.play();
        setIsPlaying(true);

        // Start playback timer
        playbackIntervalRef.current = setInterval(() => {
          if (videoElement) {
            setPlaybackTime(Math.floor(videoElement.currentTime));
          }
        }, 250); // Update 4 times per second for smoother display
      }
    },
    [isPlaying]
  );

  const skipForward = useCallback((videoElement: HTMLVideoElement | null) => {
    if (!videoElement) return;
    const newTime = Math.min(
      videoElement.duration,
      videoElement.currentTime + 10
    );
    videoElement.currentTime = newTime;
  }, []);

  const rewind = useCallback((videoElement: HTMLVideoElement | null) => {
    if (!videoElement) return;
    const newTime = Math.max(0, videoElement.currentTime - 10);
    videoElement.currentTime = newTime;
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);

    // Clear playback timer when video ends
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
  }, []);

  // Get user media stream
  useEffect(() => {
    async function getMediaStream() {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        setStream(mediaStream);
        setErrorMessage(null);
      } catch (err: any) {
        if (err.name === "NotAllowedError") {
          setErrorMessage("Camera permission denied");
        } else if (err.name === "NotFoundError") {
          setErrorMessage("No camera found");
        } else if (err.name === "NotReadableError") {
          setErrorMessage("Camera is being used by another application");
        } else {
          setErrorMessage(`Error accessing media: ${err.message}`);
        }
        console.error("Error accessing media devices:", err);
      }
    }

    getMediaStream();

    // Cleanup function
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Set video source when stream is available
  useEffect(() => {
    if (videoRef.current && stream && !mediaBlobUrl) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, mediaBlobUrl]);

  // Clear srcObject when switching to recorded video
  useEffect(() => {
    if (videoRef.current && mediaBlobUrl) {
      videoRef.current.srcObject = null;
    }
  }, [mediaBlobUrl]);

  // Start recording function
  const startRecording = useCallback(() => {
    if (!stream) {
      setErrorMessage(
        "No camera access. Please allow camera access and try again."
      );
      return;
    }

    // Reset recording state
    setRecordedChunks([]);
    setMediaBlobUrl(null);
    setRecordingTime(0);
    startTimeRef.current = Date.now();
    setIsRecording(true);

    // Create media recorder
    try {
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp9,opus",
      });
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available event
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          setRecordedChunks((prev) => [...prev, event.data]);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunks, {
          type: "video/webm",
        });
        const url = URL.createObjectURL(blob);
        setMediaBlobUrl(url);
      };

      // Handle recording error
      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setErrorMessage("Error during recording");
        forceStopRecording();
      };

      // Start recording
      mediaRecorder.start(100); // Collect data in 100ms chunks

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsedSeconds = Math.floor(
            (Date.now() - startTimeRef.current) / 1000
          );
          setRecordingTime(elapsedSeconds);

          // Auto-stop recording after maxRecordingDurationSeconds
          if (elapsedSeconds >= maxRecordingDurationSeconds) {
            console.log(
              `Auto-stopping recording after ${maxRecordingDurationSeconds} seconds`
            );
            forceStopRecording();
          }
        }
      }, 1000);

      // Set a backup timeout to ensure recording stops
      const maxDurationMs = maxRecordingDurationSeconds * 1000;
      setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          console.log(
            `Backup timeout: forcing stop recording after ${maxRecordingDurationSeconds} seconds`
          );
          forceStopRecording();
        }
      }, maxDurationMs + 500); // Add a small buffer to ensure the interval has a chance to run first
    } catch (err) {
      console.error("Error creating MediaRecorder:", err);
      setErrorMessage(
        "Could not start recording. Your browser may not support this feature."
      );
    }
  }, [stream, maxRecordingDurationSeconds, recordedChunks]);

  // Force stop recording regardless of isRecording state
  const forceStopRecording = useCallback(() => {
    console.log("Force stopping recording");
    console.log(
      "mediaRecorderRef.current state:",
      mediaRecorderRef.current?.state
    );

    if (mediaRecorderRef.current) {
      try {
        // Only call stop() if the state is not 'inactive'
        if (mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
          console.log("Recording force stopped successfully");
        } else {
          console.log("MediaRecorder already inactive, skipping stop call");
        }
      } catch (err) {
        console.error("Error stopping MediaRecorder:", err);
      }

      // Clear timer interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Update state
      setIsRecording(false);
      startTimeRef.current = null;
    }
  }, []);

  // Regular stop recording function (used by UI buttons)
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      forceStopRecording();
    } else {
      console.log("Cannot stop recording: not recording or no media recorder");
    }
  }, [isRecording, forceStopRecording]);

  // Create blob URL from recorded chunks when recording stops
  useEffect(() => {
    if (recordedChunks.length > 0 && !isRecording) {
      const blob = new Blob(recordedChunks, {
        type: "video/webm",
      });
      const url = URL.createObjectURL(blob);
      setMediaBlobUrl(url);

      // Reset playback time when creating a new recording
      setPlaybackTime(0);
    }
  }, [recordedChunks, isRecording]);

  // Handle save recording
  const handleSave = useCallback(() => {
    if (!mediaBlobUrl || recordedChunks.length === 0) return;

    const blob = new Blob(recordedChunks, {
      type: "video/webm",
    });

    const fileName = `video-recording-${Date.now()}.webm`;
    const fileType = "video/webm";
    const videoFile = new File([blob], fileName, { type: fileType });

    onSave(videoFile);
    onClose();
  }, [mediaBlobUrl, recordedChunks, onSave, onClose]);

  // Cleanup function
  useEffect(() => {
    return () => {
      // Stop all tracks
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Clear timers
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }

      // Revoke any created object URLs
      if (mediaBlobUrl) {
        URL.revokeObjectURL(mediaBlobUrl);
      }
    };
  }, [stream, mediaBlobUrl]);

  return (
    <>
      <div className="p-6 flex flex-col items-center max-h-[calc(100vh-200px)] overflow-y-auto">
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {errorMessage}
          </div>
        )}

        <div className="w-full mb-[24px] bg-black rounded-lg overflow-hidden relative h-[450px]">
          <div className="w-full h-full">
            {mediaBlobUrl && !isRecording ? (
              <video
                key="playback-video"
                ref={videoRef}
                src={mediaBlobUrl}
                controls={false}
                autoPlay={isPlaying}
                playsInline
                onEnded={handleEnded}
                onTimeUpdate={(e) =>
                  setPlaybackTime(Math.floor(e.currentTarget.currentTime))
                }
                className="w-full h-full object-cover"
              />
            ) : stream ? (
              <video
                key="preview-video"
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
                <Video className="w-16 h-16 text-gray-300" />
                <p className="text-white mt-4">Waiting for camera access...</p>
              </div>
            )}
          </div>

          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              <span>Recording</span>
            </div>
          )}

          <div className="absolute bottom-3 flex flex-1 w-full h-[64px] justify-center items-center gap-2 px-3 py-1">
            <div className="flex w-full items-center gap-2 justify-between">
              <span className="text-[24px] font-medium text-white">
                {isRecording ? (
                  <span>{formatTime(recordingTime)}</span>
                ) : mediaBlobUrl && isPlaying ? (
                  <span>{formatTime(playbackTime)}</span>
                ) : (
                  <span>00:00</span>
                )}
              </span>

              <div>
                {mediaBlobUrl && !isRecording ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="cursor-pointer"
                      onClick={() => rewind(videoRef.current)}
                    >
                      <Rewind10SecSVG size={48} color="#fff" />
                    </div>
                    <div
                      className="cursor-pointer"
                      onClick={() => togglePlayback(videoRef.current)}
                    >
                      {isPlaying ? (
                        <PauseCircle className="w-12 h-12 ml-1 text-[#C53921]" />
                      ) : (
                        <PlayCircle className="w-12 h-12 ml-1 text-[#C53921]" />
                      )}
                    </div>
                    <div
                      className="cursor-pointer"
                      onClick={() => skipForward(videoRef.current)}
                    >
                      <Forward10SecSVG size={48} color="#fff" />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    disabled={!stream}
                  >
                    {isRecording ? <PauseButtonSVG /> : <RecordButtonSVG />}
                  </button>
                )}
              </div>

              <span className="text-[24px] font-medium text-white">
                {isRecording ? (
                  <span>
                    -
                    {formatTime(
                      Math.max(0, maxRecordingDurationSeconds - recordingTime)
                    )}
                  </span>
                ) : (
                  <span>-{formatTime(maxRecordingDurationSeconds)}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
        <Button
          onClick={() => setShowConfirmCancelModal(true)}
          variant="light"
          size="md"
          type="button"
          className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          type="button"
          onClick={handleSave}
          disabled={!mediaBlobUrl}
          className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
        >
          Upload
        </Button>
      </div>

      <Modal
        isOpen={showConfirmCancelModal}
        onClose={() => setShowConfirmCancelModal(false)}
        title="Leave"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] text-center mb-8">
            Are you sure you want to exit the recording,
            <br />
            {mediaBlobUrl && " Your recording will be lost"}
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setShowConfirmCancelModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => {
              setShowConfirmCancelModal(false);
              onClose();
            }}
          >
            Yes, Cancel
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default VideoRecordingModal;
